import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Checkbox, Select, Space, TreeSelect } from 'antd'
import { flow, isNil, map, values } from 'lodash'
import PropTypes from 'prop-types'

import { PermissionGuard } from '../../../common/components/routes'
import { ToggleConfirmButtons } from '../../../common/components/sections'
import { DROPDOWN_MIN_WIDTH, PERMISSIONS } from '../../../common/constants'
import {
  handleTreeSelectDropdownItemCurationDisabledColor,
  handleTreeSelectDropdownSearchHighlights,
} from '../../../common/helpers'
import { idPropType } from '../../../common/prop-types'
import { filterSelectOptions } from '../../../common/utils'

import { DICTIONARY_WORD_TYPES } from '../../../dictionary-words/constants'
import { hasCheckedKeywords } from '../../helpers'
import {
  changeCheckedKeywordsLanguage,
  deleteCheckedKeywords,
  toggleTransferKeywordsToKeywordNode,
  transferKeywordsToIngredientKeywordNode,
  transferKeywordsToKeywordNode,
  triggerTransferKeywordsToDictionaryType,
} from '../../redux/actions'

const IngredientKeywordActionButtons = ({
  ingredientId,
  isDirty,
  fetchIngredientKeywordsById,
  ingredientOptions,
  languageOptions,
  mainCheckedKeywordKeysGroupedByType,
  supportingCheckedKeywordKeysGroupedByType,
  updateTransferedKeywords,
  transferIngredientKeywordsToDictionaryType,
  setIsMainTableCheckable,
  setIsSupportingTableShown,
}) => {
  const dispatch = useDispatch()
  const [ingredientIdToTransferTo, setIngredientIdToTransferTo] = useState(null)

  const [dictionaryTypeToTransferTo, setDictionaryTypeToTransferTo] =
    useState(null)

  const [isFinishedCleaningUp, setIsFinishedCleaningUp] = useState(false)
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isDeleteActive, setIsDeleteActive] = useState(false)
  const [isTransferActive, setIsTransferActive] = useState(false)
  const [isAssignLanguageActive, setIsAssignLanguageActive] = useState(false)
  const [languageId, setLanguageId] = useState(null)

  const [isRemovingLanguageChecked, setIsRemovingLanguageChecked] =
    useState(false)

  const { keywordsToTransfer, isFinishedTransfering } = useSelector(
    store => store.ingredients.ingredient?.keywords?.transfer || {},
  )

  const isConfirmMoveOrTransferButtonEnabled = useMemo(() => {
    if (!isNil(dictionaryTypeToTransferTo)) {
      return hasCheckedKeywords(mainCheckedKeywordKeysGroupedByType)
    }

    return (
      hasCheckedKeywords(mainCheckedKeywordKeysGroupedByType) &&
      hasCheckedKeywords(supportingCheckedKeywordKeysGroupedByType)
    )
  }, [
    dictionaryTypeToTransferTo,
    mainCheckedKeywordKeysGroupedByType,
    supportingCheckedKeywordKeysGroupedByType,
  ])

  const isConfirmDeleteButtonEnabled = useMemo(
    () => hasCheckedKeywords(mainCheckedKeywordKeysGroupedByType),
    [mainCheckedKeywordKeysGroupedByType],
  )

  const isConfirmAssignLanguageButtonEnabled = useMemo(
    () =>
      (isRemovingLanguageChecked || !isNil(languageId)) &&
      hasCheckedKeywords(mainCheckedKeywordKeysGroupedByType),
    [
      isRemovingLanguageChecked,
      languageId,
      mainCheckedKeywordKeysGroupedByType,
    ],
  )

  const dictionaryTypeOptions = useMemo(
    () =>
      map(values(DICTIONARY_WORD_TYPES), ({ value, label }) => ({
        value,
        label,
      })),
    [],
  )

  const handleOnChangeIngredientIdToTransferTo = useCallback(
    ingredientId => {
      setIngredientIdToTransferTo(ingredientId)

      if (!isNil(ingredientId)) {
        fetchIngredientKeywordsById(ingredientId)
      }
    },
    [fetchIngredientKeywordsById],
  )

  const handleOnChangeDictionaryTypeToTransferTo = useCallback(
    dictionaryType => {
      setDictionaryTypeToTransferTo(dictionaryType)
    },
    [],
  )

  const handleOnToggle = useCallback(
    (isToggleActive, setToggleActive) => {
      if (isToggleActive) {
        setIsFinishedCleaningUp(true)
        setIsMainTableCheckable(false)
      } else {
        setToggleActive(true)
        setIsMainTableCheckable(true)
      }
    },
    [setIsMainTableCheckable],
  )

  const handleOnToggleDelete = useCallback(
    () => handleOnToggle(isDeleteActive, setIsDeleteActive),
    [handleOnToggle, isDeleteActive],
  )

  const handleOnConfirmDelete = useCallback(() => {
    dispatch(deleteCheckedKeywords())
    handleOnToggle(true, setIsDeleteActive)
  }, [dispatch, handleOnToggle])

  const handleOnToggleMove = useCallback(() => {
    dispatch(toggleTransferKeywordsToKeywordNode())
    handleOnToggle(isMoveActive, setIsMoveActive)
  }, [dispatch, handleOnToggle, isMoveActive])

  const handleOnConfirmMove = useCallback(() => {
    dispatch(transferKeywordsToKeywordNode())
    handleOnToggle(true, setIsMoveActive)
  }, [dispatch, handleOnToggle])

  const handleOnToggleTransfer = useCallback(() => {
    handleOnToggle(isTransferActive, setIsTransferActive)
  }, [handleOnToggle, isTransferActive])

  const handleOnConfirmTransfer = useCallback(() => {
    if (isNil(dictionaryTypeToTransferTo)) {
      dispatch(transferKeywordsToIngredientKeywordNode())
    } else {
      dispatch(triggerTransferKeywordsToDictionaryType())
    }
    handleOnToggle(true, isTransferActive)
  }, [dictionaryTypeToTransferTo, dispatch, handleOnToggle, isTransferActive])

  const handleOnToggleLanguageAssignment = useCallback(() => {
    setIsRemovingLanguageChecked(false)
    handleOnToggle(isAssignLanguageActive, setIsAssignLanguageActive)
  }, [handleOnToggle, isAssignLanguageActive])

  const handleOnConfirmLanguageAssignment = useCallback(() => {
    dispatch(
      changeCheckedKeywordsLanguage(
        isRemovingLanguageChecked ? null : languageId,
      ),
    )

    setIsRemovingLanguageChecked(false)
    handleOnToggle(true, isAssignLanguageActive)
  }, [
    dispatch,
    handleOnToggle,
    isAssignLanguageActive,
    isRemovingLanguageChecked,
    languageId,
  ])

  const onCheckboxChange = useCallback(
    event => setIsRemovingLanguageChecked(event.target.checked),
    [],
  )

  const handleDropdownRender = useMemo(
    () =>
      flow([
        handleTreeSelectDropdownItemCurationDisabledColor,
        handleTreeSelectDropdownSearchHighlights,
      ]),
    [],
  )

  useEffect(() => {
    const shouldShowSupportingTable =
      isMoveActive || (isTransferActive && !isNil(ingredientIdToTransferTo))

    setIsSupportingTableShown(shouldShowSupportingTable)
  }, [
    ingredientIdToTransferTo,
    isMoveActive,
    isTransferActive,
    setIsSupportingTableShown,
  ])

  useEffect(() => {
    if (isFinishedCleaningUp) {
      setIsMoveActive(false)
      setIsDeleteActive(false)
      setIsTransferActive(false)
      setIsAssignLanguageActive(false)
      setIngredientIdToTransferTo(null)
      setDictionaryTypeToTransferTo(null)
      setLanguageId(null)
      setIsFinishedCleaningUp(false)
    }
  }, [
    isFinishedCleaningUp,
    mainCheckedKeywordKeysGroupedByType,
    supportingCheckedKeywordKeysGroupedByType,
  ])

  useEffect(() => {
    if (isFinishedTransfering) {
      if (isNil(dictionaryTypeToTransferTo)) {
        updateTransferedKeywords(
          ingredientId,
          ingredientIdToTransferTo,
          keywordsToTransfer,
        )
      } else {
        transferIngredientKeywordsToDictionaryType(
          ingredientId,
          keywordsToTransfer,
          dictionaryTypeToTransferTo,
        )
      }
    }
  }, [
    isFinishedTransfering,
    ingredientIdToTransferTo,
    updateTransferedKeywords,
    ingredientId,
    keywordsToTransfer,
    dictionaryTypeToTransferTo,
    transferIngredientKeywordsToDictionaryType,
  ])

  return (
    <Space>
      {!isDeleteActive && !isTransferActive && !isAssignLanguageActive && (
        <ToggleConfirmButtons
          showConfirm={isMoveActive}
          disableConfirm={!isConfirmMoveOrTransferButtonEnabled}
          onToggle={handleOnToggleMove}
          onConfirm={handleOnConfirmMove}
          operationText="Move"
        />
      )}
      {!isMoveActive && !isTransferActive && !isAssignLanguageActive && (
        <PermissionGuard permissions={PERMISSIONS.INGREDIENT.DELETE_KEYWORDS}>
          <ToggleConfirmButtons
            showConfirm={isDeleteActive}
            disableConfirm={!isConfirmDeleteButtonEnabled}
            onToggle={handleOnToggleDelete}
            onConfirm={handleOnConfirmDelete}
            operationText="Delete"
          />
        </PermissionGuard>
      )}
      {!isMoveActive && !isDeleteActive && !isAssignLanguageActive && !isDirty && (
        <Space>
          <ToggleConfirmButtons
            showConfirm={isTransferActive}
            disableConfirm={!isConfirmMoveOrTransferButtonEnabled}
            onToggle={handleOnToggleTransfer}
            onConfirm={handleOnConfirmTransfer}
            operationText="Transfer"
          />
          {isTransferActive && (
            <>
              <TreeSelect
                allowClear
                showSearch
                treeNodeFilterProp="title"
                dropdownRender={handleDropdownRender}
                treeData={ingredientOptions}
                value={ingredientIdToTransferTo}
                disabled={!isNil(dictionaryTypeToTransferTo)}
                onChange={handleOnChangeIngredientIdToTransferTo}
                placeholder="Transfer to ingredient..."
                dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              />
              <Select
                showSearch
                allowClear
                disabled={!isNil(ingredientIdToTransferTo)}
                filterOption={filterSelectOptions}
                options={dictionaryTypeOptions}
                value={dictionaryTypeToTransferTo}
                onChange={handleOnChangeDictionaryTypeToTransferTo}
                placeholder="Transfer to dictionary type..."
                dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              />
            </>
          )}
        </Space>
      )}
      {!isMoveActive && !isTransferActive && !isDeleteActive && (
        <Space>
          <ToggleConfirmButtons
            showConfirm={isAssignLanguageActive}
            disableConfirm={!isConfirmAssignLanguageButtonEnabled}
            onToggle={handleOnToggleLanguageAssignment}
            onConfirm={handleOnConfirmLanguageAssignment}
            operationText="Assign Language"
          />
          {isAssignLanguageActive && (
            <>
              <Select
                style={{ minWidth: DROPDOWN_MIN_WIDTH }}
                disabled={isRemovingLanguageChecked}
                showSearch
                filterOption={filterSelectOptions}
                options={languageOptions}
                value={languageId}
                onChange={setLanguageId}
                placeholder="Assign language to selected keywords..."
                dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
              />
              <Checkbox
                checked={isRemovingLanguageChecked}
                onChange={onCheckboxChange}
              >
                Remove Language on keywords
              </Checkbox>
            </>
          )}
        </Space>
      )}
    </Space>
  )
}

IngredientKeywordActionButtons.propTypes = {
  ingredientId: idPropType.isRequired,
  isDirty: PropTypes.bool,
  fetchIngredientKeywordsById: PropTypes.func.isRequired,
  ingredientOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  mainCheckedKeywordKeysGroupedByType: PropTypes.object,
  supportingCheckedKeywordKeysGroupedByType: PropTypes.object,
  updateTransferedKeywords: PropTypes.func.isRequired,
  transferIngredientKeywordsToDictionaryType: PropTypes.func.isRequired,
  setIsMainTableCheckable: PropTypes.func.isRequired,
  setIsSupportingTableShown: PropTypes.func.isRequired,
}

IngredientKeywordActionButtons.defaultProps = {
  isDirty: false,
  mainCheckedKeywordKeysGroupedByType: {},
  supportingCheckedKeywordKeysGroupedByType: {},
}

export default IngredientKeywordActionButtons
